<script setup>
import { toast } from 'dashboard/composables/useToast';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import SettingsListToolbar from 'dashboard/components-next/Settings/SettingsListToolbar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

const searchQuery = ref('');
const filteredRecords = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return records.value;
  return records.value.filter(
    l =>
      l.title?.toLowerCase().includes(q) ||
      l.description?.toLowerCase().includes(q)
  );
});

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const tableHeaders = computed(() => [
  t('LABEL_MGMT.LIST.TABLE_HEADER.NAME'),
  t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION'),
  t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR'),
  '',
]);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    toast.success(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    toast.error(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('LABEL_MGMT.HEADER')"
    :description="$t('LABEL_MGMT.DESCRIPTION')"
  >
    <template #actions>
      <Button
        icon="i-lucide-circle-plus"
        :label="$t('LABEL_MGMT.HEADER_BTN_TXT')"
        @click="openAddPopup"
      />
    </template>

    <SettingsListToolbar
      v-if="records.length"
      v-model="searchQuery"
      :placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
      :count-label="
        $t('LABEL_MGMT.COUNT_LABEL', { count: filteredRecords.length })
      "
    />

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!filteredRecords.length"
      :empty-message="
        records.length ? $t('LABEL_MGMT.SEARCH_404') : $t('LABEL_MGMT.LIST.404')
      "
    >
      <tr
        v-for="(label, index) in filteredRecords"
        :key="label.title"
        class="group hover:bg-n-solid-3 transition-colors"
      >
        <td class="px-5 py-3 whitespace-nowrap align-middle">
          <!-- pill igual ao chip real de etiqueta nas conversas -->
          <span
            class="inline-flex items-center gap-1.5 rounded-full border border-n-weak bg-n-solid-1 px-2.5 py-0.5 text-xs font-medium text-n-slate-12"
          >
            <span
              class="size-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            {{ label.title }}
          </span>
        </td>
        <td class="px-5 py-3 w-full max-w-0 align-middle">
          <span
            v-if="label.description"
            class="block text-sm text-n-slate-11 truncate"
            :title="label.description"
          >
            {{ label.description }}
          </span>
          <span v-else class="text-sm italic text-n-slate-8">
            {{ $t('LABEL_MGMT.NO_DESCRIPTION') }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-nowrap align-middle">
          <div class="flex items-center gap-2">
            <span
              class="size-5 rounded-md border border-n-weak shrink-0"
              :style="{ backgroundColor: label.color }"
            />
            <span class="font-mono text-xs text-n-slate-9 uppercase">
              {{ label.color }}
            </span>
          </div>
        </td>
        <td class="px-5 py-3 w-px whitespace-nowrap align-middle">
          <div
            class="flex gap-1 justify-end md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity"
          >
            <Button
              v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
              icon="i-lucide-pen"
              slate
              xs
              faded
              @click="openEditPopup(label)"
            />
            <Button
              v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              :is-loading="loading[label.id]"
              @click="openDeletePopup(label, index)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsPageLayout>
</template>
